import type { ListenResult } from './types';

export const REMOTE_AUDIO_WAIT_MS = 5000;

export type ActiveListenRequest = {
  readonly generation: number;
  readonly resolve: (result: ListenResult) => void;
  cameraToken: string | null;
  microphoneEnabled: boolean;
  representativeRobotId: string | null;
};

export type ListenReservation = {
  request: ActiveListenRequest;
  result: Promise<ListenResult>;
};

export class ListenCoordinator {
  private generation = 0;
  private active: ActiveListenRequest | null = null;

  get current() {
    return this.active;
  }

  reserve(representativeRobotId: string | null): ListenReservation | null {
    if (this.active) return null;
    let resolve!: (result: ListenResult) => void;
    const result = new Promise<ListenResult>((settle) => {
      resolve = settle;
    });
    const request: ActiveListenRequest = {
      generation: this.generation + 1,
      resolve,
      cameraToken: null,
      microphoneEnabled: false,
      representativeRobotId,
    };
    this.generation = request.generation;
    this.active = request;
    return { request, result };
  }

  isCurrent(request: ActiveListenRequest) {
    return this.active === request && request.generation === this.generation;
  }

  attachCamera(request: ActiveListenRequest, cameraToken: string | null) {
    if (!this.isCurrent(request)) return false;
    request.cameraToken = cameraToken;
    return true;
  }

  settle(result: ListenResult): ActiveListenRequest | null {
    const request = this.active;
    if (!request) return null;
    this.active = null;
    this.generation += 1;
    request.resolve(result);
    return request;
  }
}

export const acquireListenCameraLease = async (
  coordinator: ListenCoordinator,
  request: ActiveListenRequest,
  acquireCamera: () => Promise<string | null>,
  releaseCamera: (cameraToken: string) => Promise<void>,
) => {
  const cameraToken = await acquireCamera();
  if (coordinator.attachCamera(request, cameraToken)) return cameraToken;
  if (cameraToken) await releaseCamera(cameraToken);
  return null;
};

export const enableListenMicrophone = async (
  coordinator: ListenCoordinator,
  request: ActiveListenRequest,
  enableMicrophone: () => Promise<void>,
  disableMicrophone: () => Promise<void>,
) => {
  try {
    await enableMicrophone();
  } catch (error) {
    await disableMicrophone().catch(() => undefined);
    throw error;
  }
  if (coordinator.isCurrent(request)) {
    request.microphoneEnabled = true;
    return true;
  }
  await disableMicrophone().catch(() => undefined);
  return false;
};

export const hasLiveAudioTrack = (stream: MediaStream | null): stream is MediaStream => (
  !!stream && stream.getAudioTracks().some((track) => track.readyState === 'live')
);

export const waitForLiveAudioStream = (
  getStream: () => MediaStream | null,
  isActive: () => boolean,
  timeoutMs = REMOTE_AUDIO_WAIT_MS,
): Promise<MediaStream | null> => new Promise((resolve) => {
  const startedAt = Date.now();
  const inspect = () => {
    if (!isActive()) {
      resolve(null);
      return;
    }
    const stream = getStream();
    if (hasLiveAudioTrack(stream)) {
      resolve(stream);
      return;
    }
    const elapsed = Date.now() - startedAt;
    if (elapsed >= timeoutMs) {
      resolve(null);
      return;
    }
    setTimeout(inspect, Math.min(50, timeoutMs - elapsed));
  };
  inspect();
});
